/**
 * TechnicalReportDialog - Detailed technical report for field technicians
 */
import React, { useState } from 'react';
import { Pole, Cable } from '../../types';
import { useONUStore } from '../../stores/onuStore';
import { useAuthStore } from '../../stores/authStore';
import { useRezimPrace } from '../../store/roleViewStore';
import { InstallationChecklistDialog } from './InstallationChecklistDialog';
import { InstallationTechDialog } from './InstallationTechDialog';
import { getFiberColorInBuffer } from '../../utils/fiberColors';
import { indexPortovSplitterov, portNaVlakne } from '../../utils/zakaznickeVlakna';
import './Dialog.css';
import './TechnicalReportDialog.css';

interface TechnicalReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  pole: Pole;
  cables: Cable[];
  poles?: Pole[];
}

// Helper function to get fiber color name
const getFiberColorName = (fiberNumber: number): string => {
  return getFiberColorInBuffer(fiberNumber, 24);
};

const SPLITTER_LOSS: Record<string, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 13.5,
  '1:32': 17.0,
  '1:64': 20.5,
};

export const TechnicalReportDialog: React.FC<TechnicalReportDialogProps> = ({
  isOpen,
  onClose,
  pole,
  cables,
  poles = [],
}) => {
  const { onus } = useONUStore();
  const { isAdmin } = useAuthStore();
  const { jePripajanie } = useRezimPrace();
  const [showChecklist, setShowChecklist] = useState(false);

  if (!isOpen) return null;

  // Get ONUs connected to this pole
  const poleONUs = onus.filter(onu => onu.pole_id === pole.id);

  // Helper function to get ONU connected to a specific splitter port
  const getONUForPort = (splitterIndex: number, portNumber: number, poleId: number = pole.id) => {
    return onus.find(
      onu => onu.pole_id === poleId
        && onu.splitter_index === splitterIndex && onu.splitter_port === portNumber
    );
  };

  // Zákaznícke kazety sú na VIRTUÁLNYCH stĺpoch (dvojča reálneho stĺpa) a
  // správa o nich doteraz nevedela — technik tam zapísal zákazníka a v správe
  // po ňom nebolo ani stopy. Doplnené 2026-09-30.
  const virtualneDvojcata = (poles || []).filter(
    (p) => p.parent_pole_id === pole.id && p.is_virtual
  );

  // Splittery z tohto stĺpa aj z jeho zákazníckych kaziet, v jednom zozname.
  const vsetkySplittery: { stlp: Pole; index: number; splitter: any; zakaznicka: boolean }[] = [
    ...((pole.cassette?.splitters || []) as any[]).map((sp, i) => ({
      stlp: pole, index: i, splitter: sp, zakaznicka: false,
    })),
    ...virtualneDvojcata.flatMap((vp) =>
      ((vp.cassette?.splitters || []) as any[]).map((sp, i) => ({
        stlp: vp, index: i, splitter: sp, zakaznicka: true,
      }))
    ),
  ];

  // Zákazník na výstupe: prednosť má to, čo zapísal technik do kazety
  // (installation/connect), ONU je náhradné riešenie. Predtým sa čítalo LEN
  // z ONU, takže v projektoch bez ONU záznamov bol stĺpec prázdny.
  const zakaznikNaVystupe = (output: any, splitterIndex: number,
                             outIndex: number, poleId: number) => {
    if (output?.customer_name) {
      return {
        meno: String(output.customer_name).trim(),
        telefon: output.customer_phone || null,
        adresa: output.customer_address || null,
        osadil: output.installed_by || null,
        kedy: output.installed_at || null,
        zdroj: 'kazeta' as const,
      };
    }
    const onu = getONUForPort(splitterIndex, outIndex + 1, poleId);
    if (onu) {
      return {
        meno: onu.customer_name || onu.name, telefon: null, adresa: null,
        osadil: null, kedy: null, zdroj: 'onu' as const,
      };
    }
    return null;
  };

  // Všetci pripojení zákazníci na tomto stĺpe — pre samostatný prehľad.
  const zakaznici = vsetkySplittery.flatMap(({ stlp, index, splitter }) =>
    (splitter.outputs || []).map((o: any, oi: number) => {
      const z = zakaznikNaVystupe(o, index, oi, stlp.id);
      return z ? { ...z, splitter, splitterIndex: index, vystup: oi + 1, stlp } : null;
    }).filter(Boolean)
  ) as any[];

  // Prípojky k zákazníkom — koniec vlákna, ktoré odtiaľto ide k domu. Splitter,
  // z ktorého vlákno prišlo, môže stáť na inom stĺpiku, takže v správe musí byť
  // napísané kde a kto je na druhom konci; inak to z papiera nikto nevyčíta.
  const portyNaVlaknach = indexPortovSplitterov(poles || []);
  const pripojky = [
    ...(((pole.cassette as any)?.drops || []) as any[]).map((d) => ({ d, kde: pole })),
    ...virtualneDvojcata.flatMap((vp) =>
      (((vp.cassette as any)?.drops || []) as any[]).map((d) => ({ d, kde: vp })),
    ),
  ].map(({ d, kde }) => ({
    ...d,
    kde,
    port: portNaVlakne(portyNaVlaknach, d.cable_id, d.fiber),
  }));

  // Find connected cables
  const connectedCables = cables.filter(
    (c) => c.from_pole_id === pole.id || c.to_pole_id === pole.id
  );

  const getPoleName = (poleId: number): string => {
    const foundPole = poles.find((p) => p.id === poleId);
    return foundPole ? foundPole.name : `Stĺpik #${poleId}`;
  };

  const getCableName = (cableId: number): string => {
    const cable = cables.find((c) => c.id === cableId);
    if (!cable) return `Kábel #${cableId}`;

    const fromPoleName = getPoleName(cable.from_pole_id);
    const toPoleName = getPoleName(cable.to_pole_id);

    return `Kábel #${cableId} (${fromPoleName} → ${toPoleName})`;
  };

  const getCableDirection = (cable: Cable): string => {
    if (cable.from_pole_id === pole.id) {
      return 'Odchádzajúci';
    } else {
      return 'Prichádzajúci';
    }
  };

  const printReport = () => {
    window.print();
  };

  // Get current date/time for report
  const reportDate = new Date().toLocaleString('sk-SK', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <>
      <div className="dialog-overlay" onClick={onClose}>
        <div className="dialog technical-report-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="dialog-header no-print">
            <h2>📋 Technická správa - {pole.name}</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className="btn-checklist"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowChecklist(true);
                }}
                title="Otvoriť inštalačný checklist"
              >
                ✅ Checklist
              </button>
              <button className="dialog-close" onClick={onClose}>
                ✕
              </button>
            </div>
          </div>

        <div className="dialog-content technical-report-content">
          {/* Print Header */}
          <div className="print-only report-print-header">
            <h1>TECHNICKÁ SPRÁVA - INŠTALÁCIA OPTIKY</h1>
            <div className="report-meta">
              <div>Lokalita: <strong>{pole.name}</strong></div>
              <div>Dátum: <strong>{reportDate}</strong></div>
              <div>Technik: ___________________</div>
            </div>
          </div>
          {/* Pole Information */}
          <section className="report-section">
            <h3>📍 Informácie o stĺpiku</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Názov:</span>
                <span className="info-value">{pole.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Typ:</span>
                <span className="info-value">{pole.pole_type}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Poloha:</span>
                <span className="info-value">
                  {pole.latitude ? (typeof pole.latitude === 'number' ? pole.latitude.toFixed(6) : parseFloat(pole.latitude).toFixed(6)) : 'N/A'}, {pole.longitude ? (typeof pole.longitude === 'number' ? pole.longitude.toFixed(6) : parseFloat(pole.longitude as string).toFixed(6)) : 'N/A'}
                </span>
              </div>
              {pole.expected_customers && pole.expected_customers > 0 && (
                <div className="info-item">
                  <span className="info-label">Očakávaný počet zákazníkov:</span>
                  <span className="info-value">{pole.expected_customers}</span>
                </div>
              )}
            </div>
          </section>

          {/* Connected Cables */}
          <section className="report-section">
            <h3>🔌 Pripojené káble ({connectedCables.length})</h3>
            {connectedCables.length === 0 ? (
              <p className="no-data">Žiadne káble nie sú pripojené</p>
            ) : (
              <div className="cables-list">
                {connectedCables.map((cable) => (
                  <div key={cable.id} className="cable-item">
                    <div className="cable-header">
                      <span className="cable-name">{getCableName(cable.id)}</span>
                      <span className={`cable-direction ${getCableDirection(cable).toLowerCase()}`}>
                        {getCableDirection(cable)}
                      </span>
                    </div>
                    <div className="cable-details">
                      <div className="cable-detail">
                        <span className="detail-label">Typ:</span>
                        <span className="detail-value">{cable.cable_type}</span>
                      </div>
                      <div className="cable-detail">
                        <span className="detail-label">Dĺžka:</span>
                        <span className="detail-value">
                          {cable.length_meters ? `${cable.length_meters} m` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Cassette Information */}
          {/* Pripojení zákazníci — zhrnutie toho, čo technik zapísal pri montáži.
              Predtým sa čítalo len z ONU záznamov, takže v projektoch, kde
              technik zapisoval priamo do kazety, tu nebolo nič. */}
          {zakaznici.length > 0 && (
            <section className="report-section">
              <h3>👤 PRIPOJENÍ ZÁKAZNÍCI ({zakaznici.length})</h3>
              <div className="table-scroll-wrapper">
                <table className="tech-table">
                  <thead>
                    <tr>
                      <th>Zákazník</th>
                      <th>Telefón</th>
                      <th>Adresa</th>
                      <th>Splitter</th>
                      <th>Vstup</th>
                      <th>Výstup</th>
                      <th>Osadil</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zakaznici.map((z: any, i: number) => (
                      <tr key={i}>
                        <td><strong>{z.meno}</strong></td>
                        <td>{z.telefon || '-'}</td>
                        <td>{z.adresa || '-'}</td>
                        <td>
                          {z.splitter.type}
                          {z.stlp.id !== pole.id && (
                            <span style={{ color: '#666' }}> · {z.stlp.name}</span>
                          )}
                        </td>
                        <td>
                          {z.splitter.input_source_type === 'olt_port'
                            ? `OLT port ${z.splitter.input_olt_port}`
                            : z.splitter.input_cable_id
                            ? `${getCableName(z.splitter.input_cable_id)}, vlákno ${z.splitter.input_fiber || '?'}`
                            : '-'}
                        </td>
                        <td>
                          #{z.vystup}
                          <span className="fiber-color-cell"> ({getFiberColorName(z.vystup)})</span>
                        </td>
                        <td>
                          {z.osadil || '-'}
                          {z.kedy && (
                            <span style={{ color: '#666' }}>
                              {' '}· {new Date(z.kedy).toLocaleDateString('sk-SK')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {pripojky.length > 0 && (
            <section className="report-section">
              <h3>🏠 PRÍPOJKY K ZÁKAZNÍKOM ({pripojky.length})</h3>
              <p className="report-hint">
                Vlákno, ktoré z tohto stĺpika ide k domu. Zákaznícky splitter,
                z ktorého prišlo, môže stáť na inom stĺpiku — stĺpec „Port splittra"
                hovorí kde.
              </p>
              <div className="table-scroll-wrapper">
                <table className="tech-table">
                  <thead>
                    <tr>
                      <th>Kábel</th>
                      <th>Vlákno</th>
                      <th>Kazeta</th>
                      <th>Port splittra</th>
                      <th>Zákazník</th>
                      <th>Popis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pripojky.map((pr: any, i: number) => (
                      <tr key={i}>
                        <td>{getCableName(pr.cable_id)}</td>
                        <td>
                          {pr.fiber}
                          <span className="fiber-color-cell"> ({getFiberColorName(pr.fiber)})</span>
                        </td>
                        <td>
                          {pr.kde.id === pole.id ? 'hlavná' : 'zákaznícka'}
                          {pr.kde.id !== pole.id && (
                            <span style={{ color: '#666' }}> · {pr.kde.name}</span>
                          )}
                        </td>
                        <td>
                          {pr.port
                            ? `#${pr.port.vystup} splittra ${pr.port.splitterTyp || ''} · ${pr.port.poleName}`
                            : '— na vlákne nie je zákaznícky splitter'}
                        </td>
                        <td>
                          {pr.port
                            ? pr.port.zakaznik
                              ? <strong>{pr.port.zakaznik}</strong>
                              : <span style={{ color: '#166534', fontWeight: 700 }}>VOĽNÝ</span>
                            : '-'}
                        </td>
                        <td>{pr.label || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {(pole.cassette || vsetkySplittery.length > 0) && (
            <>
              {/* Splitters */}
              {vsetkySplittery.length > 0 && (
                <section className="report-section">
                  <h3>🔀 KONFIGURÁCIA SPLITTEROV</h3>
                  {vsetkySplittery.map(({ stlp: splStlp, index, splitter, zakaznicka }) => (
                    <div key={`${splStlp.id}-${index}`} className="splitter-table-container">
                      <h4>
                        Splitter č.{index + 1} - Typ: {splitter.type} (Útlm: {SPLITTER_LOSS[splitter.type]} dB)
                        {zakaznicka && (
                          <span style={{ fontWeight: 400, color: '#666' }}>
                            {' '}· zákaznícka kazeta ({splStlp.name})
                          </span>
                        )}
                      </h4>

                      <table className="tech-table">
                        <thead>
                          <tr>
                            <th colSpan={4} className="table-section-header">VSTUP</th>
                          </tr>
                          <tr>
                            <th>Typ zdroja</th>
                            <th>Kábel/Port</th>
                            <th>Vlákno č.</th>
                            <th>Farba</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{splitter.input_source_type === 'olt_port' ? 'OLT Port' : splitter.input_source_type === 'splitter_output' ? 'Výstup splittera' : 'Kábel'}</td>
                            <td>
                              {splitter.input_source_type === 'olt_port'
                                ? `Port ${splitter.input_olt_port}`
                                : splitter.input_source_type === 'splitter_output'
                                ? `Splitter #${(splitter.input_splitter_index || 0) + 1} Výstup ${(splitter.input_splitter_output || 0) + 1}`
                                : getCableName(splitter.input_cable_id)}
                            </td>
                            <td>{splitter.input_fiber || '-'}</td>
                            <td className="fiber-color-cell">{splitter.input_fiber ? getFiberColorName(splitter.input_fiber) : '-'}</td>
                          </tr>
                        </tbody>
                      </table>

                      <table className="tech-table">
                        <thead>
                          <tr>
                            <th colSpan={8} className="table-section-header">VÝSTUPY</th>
                          </tr>
                          <tr>
                            <th>Výstup č.</th>
                            <th>Kábel</th>
                            <th>Vlákno č.</th>
                            <th>Farba</th>
                            <th>Reverzné</th>
                            <th>Zákazník</th>
                            <th>Telefón</th>
                            <th>Stav</th>
                          </tr>
                        </thead>
                        <tbody>
                          {splitter.outputs?.map((output: any, outIndex: number) => {
                            const zak = zakaznikNaVystupe(output, index, outIndex, splStlp.id);

                            // Check if this is a reverse fiber
                            const isReverse = output.cable_id && cables.find((c: Cable) =>
                              c.id === output.cable_id && c.to_pole_id === splStlp.id
                            );

                            // Pri zákazníckom splitteri výstup nemá uložené vlákno —
                            // farba sa odvodí z poradia výstupu, presne ako to robí
                            // obrazovka technika (#1 modrá, #2 oranžová…).
                            const cisloVlakna = output.fiber || (zakaznicka ? outIndex + 1 : null);

                            return (
                            <tr key={outIndex}>
                              <td><strong>{outIndex + 1}</strong></td>
                              <td>{output.cable_id ? getCableName(output.cable_id) : <em>Nepripojené</em>}</td>
                              <td>{cisloVlakna || '-'}</td>
                              <td className="fiber-color-cell">{cisloVlakna ? getFiberColorName(cisloVlakna) : '-'}</td>
                              <td>
                                {isReverse ? (
                                  <span style={{
                                    color: '#ff9800',
                                    fontWeight: 600,
                                    backgroundColor: '#fff3e0',
                                    padding: '2px 6px',
                                    borderRadius: '4px'
                                  }}>
                                    ⬅️ ÁNO
                                  </span>
                                ) : (
                                  output.cable_id ? '→ NIE' : '-'
                                )}
                              </td>
                              <td>
                                {zak ? (
                                  <strong style={{ color: '#10b981' }}>
                                    {zak.zdroj === 'onu' ? '📡 ' : '👤 '}{zak.meno}
                                  </strong>
                                ) : (
                                  <em>-</em>
                                )}
                              </td>
                              <td>
                                {zak?.telefon ? zak.telefon : <em>-</em>}
                              </td>
                              <td>{zak ? '✓ Obsadené' : output.cable_id ? '✓ Pripojené' : '○ Voľné'}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </section>
              )}

              {/* Splices */}
              {pole.cassette?.splices && pole.cassette.splices.length > 0 && (
                <section className="report-section">
                  <h3>🔗 ZOZNAM ZVAROV</h3>
                  <div className="table-scroll-wrapper">
                    <table className="tech-table splice-table">
                      <thead>
                        <tr>
                          <th rowSpan={2}>#</th>
                          <th colSpan={3}>Z (Strana A)</th>
                          <th colSpan={3}>DO (Strana B)</th>
                          <th rowSpan={2}>Útlm (dB)</th>
                          <th rowSpan={2}>Overené</th>
                        </tr>
                        <tr>
                          <th>Zdroj</th>
                          <th>Vlákno č.</th>
                          <th>Farba</th>
                          <th>Kábel</th>
                          <th>Vlákno č.</th>
                          <th>Farba</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pole.cassette!.splices.map((splice: any, index: number) => (
                          <tr key={index}>
                            <td><strong>{index + 1}</strong></td>
                            <td>
                              {splice.from_source_type === 'olt_port'
                                ? `OLT Port ${splice.from_olt_port}`
                                : getCableName(splice.from_cable_id)}
                            </td>
                            <td>{splice.from_fiber || '-'}</td>
                            <td className="fiber-color-cell">{splice.from_fiber ? getFiberColorName(splice.from_fiber) : '-'}</td>
                            <td>{getCableName(splice.to_cable_id)}</td>
                            <td>{splice.to_fiber}</td>
                            <td className="fiber-color-cell">{getFiberColorName(splice.to_fiber)}</td>
                            <td>{splice.loss_db || 0.1}</td>
                            <td className="checkbox-cell">☐</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Pass-through Cables */}
              {pole.cassette?.pass_through_cables && pole.cassette.pass_through_cables.length > 0 && (
                <section className="report-section">
                  <h3>➡️ PRIECHODNÉ PREPOJENIA</h3>
                  {pole.cassette!.pass_through_cables.map((pt: any, index: number) => (
                    <div key={index} className="passthrough-table-container">
                      <h4>Priechod #{index + 1}: {getCableName(pt.from_cable_id)} → {getCableName(pt.to_cable_id)}</h4>
                      <table className="tech-table">
                        <thead>
                          <tr>
                            <th>Vlákno č.</th>
                            <th>Farba</th>
                            <th>Stav</th>
                            <th>Odpich</th>
                            <th>Overené</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pt.fibers && pt.fibers.length > 0 ? (
                            pt.fibers.map((fiber: any, fIndex: number) => (
                              <tr key={fIndex}>
                                <td><strong>{fiber.fiber_number}</strong></td>
                                <td className="fiber-color-cell">{getFiberColorName(fiber.fiber_number)}</td>
                                <td>{fiber.is_tapped ? '⚡ Odpich' : '→ Priechod'}</td>
                                <td>
                                  {fiber.is_tapped
                                    ? `${getCableName(fiber.tap_to_cable_id)} Vlákno #${fiber.tap_to_fiber}`
                                    : '-'}
                                </td>
                                <td className="checkbox-cell">☐</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5}><em>Žiadna konfigurácia vlákien</em></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </section>
              )}
            </>
          )}

          {!pole.cassette && (
            <section className="report-section">
              <p className="no-data">Na tomto stĺpiku nie je nainštalovaná kazeta</p>
            </section>
          )}

          {/* Notes Section */}
          <section className="report-section notes-section">
            <h3>📝 POZNÁMKY / PROBLÉMY</h3>
            <div className="notes-area">
              <div className="note-line"></div>
              <div className="note-line"></div>
              <div className="note-line"></div>
              <div className="note-line"></div>
              <div className="note-line"></div>
            </div>
          </section>

          {/* Signature Section */}
          {/* Photo Documentation Section */}
          {pole.photos && pole.photos.length > 0 && (
            <section className="report-section photo-documentation-section">
              <h3>📸 FOTODOKUMENTÁCIA</h3>
              <div className="photo-grid-report">
                {pole.photos
                  .sort((a, b) => {
                    // Primary photo first
                    if (a.is_primary) return -1;
                    if (b.is_primary) return 1;
                    // Then by upload date
                    return new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime();
                  })
                  .map((photo, index) => (
                    <div key={photo.id} className="report-photo-item">
                      {photo.is_primary && <div className="photo-primary-badge">⭐ Hlavná fotka</div>}
                      <div className="photo-number">Fotka #{index + 1}</div>
                      <div className="photo-image-container">
                        <img src={photo.url} alt={photo.caption || `Fotka ${index + 1}`} />
                      </div>
                      {photo.caption && (
                        <div className="photo-caption">
                          <strong>Popis:</strong> {photo.caption}
                        </div>
                      )}
                      <div className="photo-metadata">
                        <small>
                          Nahraté: {new Date(photo.uploaded_at).toLocaleString('sk-SK')}
                        </small>
                        {photo.uploaded_by && photo.uploaded_by !== 'unknown' && (
                          <small>Technik: {photo.uploaded_by}</small>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          )}

          <section className="report-section signature-section print-only">
            <div className="signature-row">
              <div className="signature-box">
                <div className="signature-label">Podpis technika:</div>
                <div className="signature-line"></div>
                <div className="signature-date">Dátum: _______________</div>
              </div>
              <div className="signature-box">
                <div className="signature-label">Podpis supervízora:</div>
                <div className="signature-line"></div>
                <div className="signature-date">Dátum: _______________</div>
              </div>
            </div>
          </section>
        </div>

        <div className="dialog-footer no-print">
          <button className="btn btn-secondary" onClick={printReport}>
            🖨️ Tlačiť správu
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            Zavrieť
          </button>
        </div>
      </div>
    </div>

    {showChecklist && (
      <>
        {jePripajanie ? (
          <InstallationTechDialog
            pole={pole}
            projectId={pole.project_id}
            onClose={() => setShowChecklist(false)}
          />
        ) : (
          <InstallationChecklistDialog
            pole={pole}
            onClose={() => setShowChecklist(false)}
          />
        )}
      </>
    )}
    </>
  );
};

